import {
  Alert,
  Checkbox,
  CircularProgress,
  Skeleton,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import { IEmployeeGradeInput, IEmployeeGrades } from "models/workshop";
import React, { useState } from "react";
import { useMutation } from "react-query";
import Search from "components/Search/Search";
import {
  editGrade,
  EmployeeGradesCells,
  getEmployeeGrades,
} from "framework/workshops";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BackButton from "components/UI/BackButton";
import { getUserData } from "helpers";
import { LocalStorage } from "enums/localStorage";
import { IPermission } from "models/permissions";
import EmptyContent from "components/EmptyContent";
import useFetchSection from "hooks/useFetchSection";
import { handleError } from "helpers/utils";
import { UserRoleName } from "types/auth";
import BlockIcon from "@mui/icons-material/Block";
import { SortOrder } from "constants/enums";
import MuiButton from "components/UI/MuiButton";
interface Props {
  feature: Feature;
}

const EmployeesGrades: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  const userData = getUserData();
  const { roleName } = userData;

  const [changes, setChanges] = useState<IEmployeeGradeInput[]>([]);
  const [filters, setFilters] = useState<IEmployeeGrades[]>([]);
  const [hasGradesPermission, setHasGradesPermission] = useState<boolean>(false);
  const [hasModifyPermission, setHasModifyPermission] = useState<boolean>(false);
  const { t } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const { setAlert } = useAlert();

  const editWorkshopsMutation = useMutation({
    mutationFn: (data: { createInput: IEmployeeGradeInput[] }) => {
      return editGrade(data.createInput);
    },
  });

  const checkGradesPermission = () => {
    try {
      const permissionsString = localStorage.getItem(LocalStorage.PERMISSIONS);
      
      if (!permissionsString) {
        setHasGradesPermission(false);
        setHasModifyPermission(false);
        return;
      }
      
      const permissionsObj = JSON.parse(permissionsString);
      // Convert object to array
      const permissions: IPermission[] = Object.values(permissionsObj);
      
      const gradesPermission = permissions?.find(
        (p) => p.permissionName === 'WorkshopGrades'
      );
      
      const hasRead = gradesPermission?.hasReadPermission || false;
      const hasModify = gradesPermission?.hasModifyPermission || false;
      
      setHasGradesPermission(hasRead || hasModify);
      setHasModifyPermission(hasModify);
    } catch (error) {
      console.error('Error checking grades permission:', error);
      setHasGradesPermission(false);
      setHasModifyPermission(false);
    }
  };

  React.useEffect(() => {
    checkGradesPermission();
  }, []);

  const {
    loading,
    data: Grades,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getEmployeeGrades(`?workshopId=${id}`),
    fetchPermission: async () => true,
    section: "Employees grade",
    processResponse: (data) =>
      data.sort((a, b) => {
        const totalA =
          (+a?.addition || 0) +
          (+a?.involvement || 0) +
          (+a?.pretestScore || 0) +
          (+a?.posttestScore || 0);
        const totalB =
          (+b?.addition || 0) +
          (+b?.involvement || 0) +
          (+b?.pretestScore || 0) +
          (+b?.posttestScore || 0);

        return totalB - totalA;
      })
        .map((item: IEmployeeGrades, i) => {
          return { ...item, index: i };
        }),
    onSuccess: (data) => setFilters(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );
  if (error)
    return (
      <Alert severity="error">
        Something went wrong, please try again later.
      </Alert>
    );

  const handleInputChange = (
    key: string,
    value: number | boolean | string,
    enrollmentId: string
  ) => {
    // Prevent changes if user doesn't have modify permission
    if (!hasModifyPermission) {
      return;
    }
    
    if (key === "addition" || key === "involvement") {
      if (+value > 999 && +value < 1) {
        return;
      }
    }

    console.log(`Updating ${key} for enrollmentId: ${enrollmentId} with value: ${value}`);

    // First update filters to get the current row data, then update changes
    setFilters((prevFilters) => {
      // Find and update the specific row
      const updatedFilters = prevFilters.map((item) => {
        if (item.enrollmentId === enrollmentId) {
          // Calculate the value to use (capped for addition/involvement)
          let valueToUse = value;
          if (key === "addition" || key === "involvement") {
            valueToUse = Math.min(Number(value), 999);
          }
          return { ...item, [key]: valueToUse };
        }
        return item;
      });

      // Calculate total score and rank
      const rankedData = updatedFilters.map((item) => {
        const totalScore =
          (item.pretestScore || 0) +
          (item.posttestScore || 0) +
          (item.involvement || 0) +
          (item.addition || 0);
        return { ...item, totalScore };
      });

      const sortedData = [...rankedData].sort(
        (a, b) => b.totalScore - a.totalScore
      );

      sortedData.forEach((item, index) => {
        item.rank = index + 1;
        item.index = index;
      });

      const finalData = rankedData.map((item) => {
        const found = sortedData.find(
          (sortedItem) => sortedItem.enrollmentId === item.enrollmentId
        );
        return { ...item, rank: found!.rank, index: found!.index };
      });

      // Now update changes with the current row from updated filters
      setChanges((prevChanges) => {
        const updatedChanges = [...prevChanges];
        const rowIndex = updatedChanges.findIndex(
          (item) => item.id === enrollmentId
        );

        if (
          key !== "post" &&
          key !== "pre" &&
          key !== "employee" &&
          key !== "employeeAr"
        ) {
          let updatedValue = value;

          // Apply maximum value check for addition and involvement
          if (key === "addition" || key === "involvement") {
            updatedValue = Math.min(Number(updatedValue), 999);
          }

          if (rowIndex > -1) {
            updatedChanges[rowIndex] = {
              ...updatedChanges[rowIndex],
              [key]: updatedValue,
            };
          } else {
            // Get current row data from the updated filters
            const currentRow = finalData.find(f => f.enrollmentId === enrollmentId);
            if (currentRow) {
              const {
                posttestScore,
                pretestScore,
                employee,
                employeeAr,
                enrollmentId: _,
                ...filteredRow
              } = currentRow;
              updatedChanges.push({ ...filteredRow, id: enrollmentId, [key]: updatedValue });
            }
          }
        }

        return updatedChanges;
      });

      return finalData;
    });
  };

  const getAttendedBodyCells = (row: IEmployeeGrades) => {
    return Object.keys(row).map((key: string, i: number) => {

      if (key === "addition") {
        return {
          id: "addition",
          element: (
            <TableCell align={"left"} key={i}>
              <TextField
                key={`addition-${row.enrollmentId}`}
                variant="outlined"
                size="small"
                type="number"
                value={row.addition ?? ''}
                disabled={!hasModifyPermission || row?.isLocked}
                onChange={(e) => {
                  e.preventDefault();
                  handleInputChange("addition", +e.target.value, row.enrollmentId);
                }}
              />
            </TableCell>
          ),
        };
      }

      if (key === "eligibleForCertificate") {
        return {
          id: "eligibleForCertificate",
          element: (
            <TableCell align={"left"} key={i}>
              <Checkbox
                checked={row.eligibleForCertificate}
                disabled={!hasModifyPermission || row?.isLocked}
                onChange={(e) =>
                  handleInputChange(
                    "eligibleForCertificate",
                    e.target.checked,
                    row.enrollmentId
                  )
                }
                inputProps={{ "aria-label": "eligible for certificate" }}
              />
            </TableCell>
          ),
        };
      }

      if (key === "employee") {
        return {
          id: "employee",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.employee}</Typography>
            </TableCell>
          ),
        };
      }

      if (key === "pretestScore") {
        return {
          id: "pretestScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.pretestScore}</Typography>
            </TableCell>
          ),
        };
      }

      if (key === "posttestScore") {
        return {
          id: "posttestScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.posttestScore}</Typography>
            </TableCell>
          ),
        };
      }

      if (key === "workshopEvaluationScore") {
        return {
          id: "workshopEvaluationScore",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.workshopEvaluationScore ?? "-"}
              </Typography>
            </TableCell>
          ),
        };
      }

      if (key === "rank") {
        return {
          id: "rank",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.index + 1}</Typography>
            </TableCell>
          ),
        };
      }

      if (key === "involvement") {
        return {
          id: "involvement",
          element: (
            <TableCell align={"left"} key={i}>
              <TextField
                key={`involvement-${row.enrollmentId}`}
                type="number"
                variant="outlined"
                size="small"
                value={row.involvement ?? ''}
                disabled={!hasModifyPermission || row?.isLocked}
                onChange={(e) =>
                  handleInputChange("involvement", +e.target.value, row.enrollmentId)
                }
              />
            </TableCell>
          ),
        };


      }

      if (key === "note") {
        return {
          id: "note",
          element: (
            <TableCell align={"left"} key={i}>
              <TextField
                multiline
                sx={{ minWidth: '400px' }}
                rows={4}
                type="text"
                variant="outlined"
                size="small"
                value={row.note}
                disabled={!hasModifyPermission || row?.isLocked}
                onChange={(e) =>
                  handleInputChange("note", e.target.value, row.enrollmentId)
                }
              />
            </TableCell>
          ),
        };


      }

      if (key === "noteAr") {
        return {
          id: "noteAr",
          element: (
            <TableCell align={"left"} key={i}>
              <TextField
                multiline
                rows={4}
                sx={{ minWidth: '400px' }}
                type="text"
                variant="outlined"
                size="small"
                value={row.noteAr}
                disabled={!hasModifyPermission || row?.isLocked}
                onChange={(e) =>
                  handleInputChange("noteAr", e.target.value, row.enrollmentId)
                }
              />
            </TableCell>
          ),
        };


      }

      else {
        return <></>;
      }
    });
  };
  if (!hasGradesPermission) {
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );
  }

  const handleSearch = async (value: string) => {
    setChanges([]);
    if (!value) return setFilters(Grades!);
    let filtered = Grades!.filter((grade) =>
      grade.employee.toLowerCase().includes(value.toLowerCase())
    );
    setFilters(filtered);
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      await editWorkshopsMutation.mutateAsync({
        createInput: changes,
      });
      setAlert({
        open: true,
        message: "Grade updated successfully",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <div>
        <BackButton path={`/workshops/${id}`} text={t("T_back_to_workshops")} />
      </div>
      <Stack spacing={2} p={2}>
        <Stack
          spacing={2}
          p={2}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography
            variant="h3"
            color={"primary.main"}
            style={{ paddingBlock: "30px" }}
          >
            {t("T_employeeGrades")}
          </Typography>
          <MuiButton
            variant="contained"
            sx={{ bgcolor: "primary.main", color: "white" }}
            disabled={changes.length === 0 || loading2 || !hasModifyPermission}
            size="large"
            onClick={handleEdit}
          >
            {!loading2 ? t("T_save") : <CircularProgress size={10} />}
          </MuiButton>
        </Stack>
        <Search handleChange={handleSearch} />
        {filters!?.length > 0 && (
          <List
            headCells={EmployeeGradesCells}
            data={filters!}
            initialState={{
              order: SortOrder.DESC,
              orderBy: "created_at",
              page: 0,
              rowsPerPage: 100000,
            }}
            getBodyCells={getAttendedBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!Grades! || Grades!?.length === 0) && (
          <EmptyContent
            icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_attended_list")}
            message={t("T_no_attended_list_message")}
          />
        )}
      </Stack>
    </>
  );
};

export default EmployeesGrades;
